// Batch ZU — central helper for rendering history (Verlauf) rows.
//
// Diff entries in `*_history` tables store snake_case field names
// ("status", "first_name", "marketing_stage") and raw enum values
// ("signed_contract", "kaufanbot_angenommen"). The Verlauf panels in
// KundendetailsDialog / ViewPropertyDialog / AppointmentDetailsDialog
// used to show those raw — non-technical operators can't read them.
//
// This helper does two things:
//   1. fieldLabel(scope, field) → German column label
//   2. valueLabel(scope, field, value, t) → translated enum value when
//      the field is an enum, otherwise the value untouched
//
// `t` is the react-i18next translator; the helper relies on the existing
// `enums.*` namespace in apps/web/src/i18n/locales/de.json.

import type { TFunction } from 'i18next';
import { formatDateTime } from '@/lib/formatLocale';

export type HistoryScope = 'client' | 'property' | 'appointment' | 'offer';

// Maps `field_changed` (snake_case) to a German column label per scope.
// When a field isn't in the map, the raw column name is returned so the
// operator still sees something rather than nothing.
const FIELD_LABELS: Record<HistoryScope, Record<string, string>> = {
  client: {
    first_name: 'Vorname',
    last_name: 'Nachname',
    title: 'Titel',
    phone: 'Telefon',
    phone_2: 'Telefon 2',
    email: 'E-Mail',
    address: 'Adresse',
    location: 'Ort',
    postal_code: 'PLZ',
    city: 'Stadt',
    country: 'Land',
    client_type: 'Kundentyp',
    status: 'Status',
    interest_status: 'Interessen-Status',
    assigned_agent_id: 'Zuständiger Makler',
    platform: 'Plattform',
    platform_code: 'Plattform-Code',
    preferred_language: 'Sprache',
    notes: 'Notizen',
    notes_2: 'Notizen 2',
    notes_3: 'Notizen 3',
    traffic_light: 'Ampel-Status',
    do_not_call: 'Werbeanrufe untersagt',
    next_action_due: 'Nächste Aktion',
    aufnahme_lock_until: 'Übernahme-Sperre bis',
    aufnahme_lock_agent_id: 'Sperre für Makler',
    secondary_agent_id: 'Zweiter Makler',
    activity_check: 'Aktivitätsprüfung',
    activity_due_at: 'Aktivität fällig am',
  },
  property: {
    address: 'Adresse',
    street: 'Straße',
    street_number: 'Hausnummer',
    postal_code: 'PLZ',
    city: 'Stadt',
    district: 'Bezirk',
    property_type: 'Immobilien-Typ',
    building_type: 'Gebäudetyp',
    marketing_stage: 'Vermarktungs-Phase',
    purchase_price: 'Kaufpreis',
    rooms: 'Zimmer',
    bedrooms: 'Schlafzimmer',
    bathrooms: 'Badezimmer',
    living_area: 'Wohnfläche (m²)',
    plot_area: 'Grundstück (m²)',
    floor: 'Stockwerk',
    year_built: 'Baujahr',
    notes: 'Notizen',
    edi_real_number: 'EDI-Real-Nr.',
    client_id: 'Verkäufer',
    assigned_agent_id: 'Zuständiger Makler',
    ownership_locked: 'Ownership-Sperre',
  },
  appointment: {
    appointment_date: 'Termindatum',
    appointment_type: 'Termin-Art',
    status: 'Status',
    agent_id: 'Zuständiger Makler',
    property_id: 'Immobilie',
    client_id: 'Klient',
    notes: 'Notizen',
  },
  offer: {
    status: 'Status',
    offer_amount: 'Anbot-Betrag',
    valid_until: 'Gültig bis',
    notes: 'Notizen',
  },
};

// Maps `field_changed` to an `enums.<group>` namespace key so we can
// translate enum values via t(`enums.${group}.${value}`). Returns null
// for free-text fields where the value is rendered raw.
const ENUM_GROUP: Record<HistoryScope, Record<string, string>> = {
  client: {
    status: 'client_status',
    client_type: 'client_type',
    interest_status: 'interest_status',
  },
  property: {
    marketing_stage: 'marketing_stage',
    building_type: 'building_type',
    property_type: 'property_type',
  },
  appointment: {
    status: 'appointment_status',
    appointment_type: 'appointment_type',
  },
  offer: {
    status: 'offer_status',
  },
};

export function fieldLabel(scope: HistoryScope, field: string | null | undefined): string {
  if (!field) return '—';
  const label = FIELD_LABELS[scope]?.[field];
  if (label) return label;
  // Friendly fallback: snake_case → Title Case so unmapped fields still
  // read more like prose. "client_id" → "Client Id".
  return field
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export function valueLabel(
  scope: HistoryScope,
  field: string | null | undefined,
  raw: string | null | undefined,
  t: TFunction,
): string {
  if (raw == null || raw === '') return '—';
  const group = field ? ENUM_GROUP[scope]?.[field] : null;
  if (group) {
    // i18next returns the key itself when the translation is missing.
    // Use the explicit fallback path so unmapped enum values stay readable.
    const translated = t(`enums.${group}.${raw}`, raw);
    return translated;
  }
  // Booleans → checkmark/cross with German label
  if (raw === 'true') return '✓ Ja';
  if (raw === 'false') return '✗ Nein';
  // ISO date strings → de-AT locale rendering
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return formatDateTime(d);
  }
  // ms epoch strings (rare but possible) → also localize
  if (/^\d{12,14}$/.test(raw)) {
    const d = new Date(Number(raw));
    if (!Number.isNaN(d.getTime())) return formatDateTime(d);
  }
  return raw;
}

// Format "old → new" as a human sentence. Used by the AppointmentDetailsDialog
// status-row, but useful anywhere we want a single inline string instead of
// the two-pill layout.
export function diffSentence(
  scope: HistoryScope,
  field: string | null | undefined,
  oldRaw: string | null | undefined,
  newRaw: string | null | undefined,
  t: TFunction,
): string {
  const fld = fieldLabel(scope, field);
  const oldL = valueLabel(scope, field, oldRaw, t);
  const newL = valueLabel(scope, field, newRaw, t);
  if (oldRaw && newRaw) return `${fld}: ${oldL} → ${newL}`;
  if (newRaw) return `${fld}: ${newL}`;
  if (oldRaw) return `${fld}: ${oldL} entfernt`;
  return fld;
}
